import { useState, useMemo, useEffect } from "react";
import { Badge } from "../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "../../ui/table";
import {
    Search,
    ChevronLeft,
    ChevronRight,
    Users,
    Calendar,
    Shield,
    Plus,
    Edit,
    Trash2,
    MoreHorizontal,
    X,
    User,
    Crown,
    Settings,
    User2Icon
} from "lucide-react";
import { Button } from "../../ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../ui/alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../../ui/dialog";
import { Label } from "../../ui/label";
import { useToast } from "../../../hooks/use-toast";
import { RoleUser, UserInRole, UserInRoles, UserRole } from "./interface";
import { myLocalStorage } from "../../StorageHelper";
import EmptyStateCard from "../EmptyStateCard";
import useUserStore from "../../../services/userStore";
import { createCustomRole, deleteRole, getAllUserRoles, getUsers, updateCustomRole } from "./Api/userrolesapi";
import { formatDate } from "../formatTimeAgo";

export default function UserRolesDetails() {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState<UserRole | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [roleToEdit, setRoleToEdit] = useState<UserRole | null>(null);
    const [editRoleName, setEditRoleName] = useState("");
    const [editRoleDescription, setEditRoleDescription] = useState("");
    const [newRoleName, setNewRoleName] = useState("");
    const [newRoleDescription, setNewRoleDescription] = useState("");
    const [usersDialogOpen, setUsersDialogOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
    const [usersSearchTerm, setUsersSearchTerm] = useState("");
    const [usersCurrentPage, setUsersCurrentPage] = useState(1);
    const [usersPerPage] = useState(5);
    const { toast } = useToast();
    const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
    const { role, email, userId, userRoleId, userRole } = useUserStore(
        (state) => state.user,
    );
    const [roles, setRoles] = useState<UserRole[]>([]);
    const [userInRoles, setUserInRoles] = useState<UserInRoles | null>(null);
    const [filteredUsers, setFilteredUsers] = useState<UserInRole[]>([])

    const getRoleDescription = (role: UserRole) => {
        if (!role.customRole) return "-";
        return role.description?.trim() || "-";
    };

    const filteredRoles = useMemo(() => {
        if (searchTerm.length > 0)
            return roles.filter(role =>
                role.roleName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        else
            return roles
    }, [searchTerm, roles]);

    // Pagination for roles
    const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentRoles = filteredRoles.slice(startIndex, endIndex);

    const filteredUsersInRole = useMemo(() => {
        if (usersSearchTerm.length > 0) {
            const term = usersSearchTerm.toLowerCase().trim();
            return filteredUsers.filter(user => {
                const firstName = user.firstName?.toLowerCase() ?? "";
                const lastName = user.lastName?.toLowerCase() ?? "";
                const email = user.email?.toLowerCase() ?? "";
                const fullName = `${firstName} ${lastName}`.trim();
                return (
                    firstName.includes(term) ||
                    lastName.includes(term) ||
                    fullName.includes(term) ||
                    email.includes(term)
                );
            });
        }
        return filteredUsers;
    }, [usersSearchTerm, filteredUsers]);

    const usersTotalPages = Math.ceil(filteredUsersInRole.length / usersPerPage);
    const usersStartIndex = (usersCurrentPage - 1) * usersPerPage;
    const usersEndIndex = usersStartIndex + usersPerPage;
    const currentUsersInRole = filteredUsersInRole.slice(usersStartIndex, usersEndIndex);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleUsersPageChange = (page: number) => {
        setUsersCurrentPage(page);
    };

    const handleDeleteClick = (role: UserRole) => {
        setRoleToDelete(role);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (roleToDelete) {
            let response = await deleteRole(tenantName, roleToDelete.id);
            toast({
                title: "Role Deleted",
                description: `${roleToDelete.roleName} has been deleted successfully.`,
            });
            setDeleteDialogOpen(false);
            setRoleToDelete(null);
            loadUsers();
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setRoleToDelete(null);
    };

    const handleEditClick = (role: UserRole) => {
        setRoleToEdit(role);
        setEditRoleName(role.roleName);
        setEditRoleDescription(role.description || "");
        setEditDialogOpen(true);
    };

    const handleEditSave = async () => {
        if (roleToEdit && editRoleName.trim()) {
            let response = await updateCustomRole(
                tenantName,
                editRoleName,
                roleToEdit.id,
                editRoleDescription,
            );

            toast({
                title: "Role Updated",
                description: `${roleToEdit.roleName} has been updated successfully.`,
            });
            setEditDialogOpen(false);
            setRoleToEdit(null);
            setEditRoleName("");
            setEditRoleDescription("");
            loadUsers();
        }
    };

    const handleEditCancel = () => {
        setEditDialogOpen(false);
        setRoleToEdit(null);
        setEditRoleName("");
        setEditRoleDescription("");
    };

    const handleAddRole = () => {
        setAddDialogOpen(true);
    };

    const handleAddSave = async () => {
        if (newRoleName.trim()) {
            let response = await createCustomRole(tenantName, newRoleName, newRoleDescription);
            toast({
                title: "Role Created",
                description: `${newRoleName} has been created successfully.`,
            });
            setAddDialogOpen(false);
            setNewRoleName("");
            setNewRoleDescription("");
            loadUsers();
        }
    };

    const handleAddCancel = () => {
        setAddDialogOpen(false);
        setNewRoleName("");
        setNewRoleDescription("");
    };

    const handleViewUsers = (role: UserRole) => {
        if (!userInRoles) return;

        const roleUsers = userInRoles.roles.users[role.roleName] || [];


        const formattedUsers = roleUsers.map((user) => ({
            id: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            createdAt: formatDate(new Date(user.createdAt).toISOString()),
        }));

        setFilteredUsers(formattedUsers); // this triggers useMemo for filteredUsersInRole
        setSelectedRole(role);
        setUsersDialogOpen(true);
        setUsersSearchTerm("");
        setUsersCurrentPage(1);
    };


    const loadUsers = async () => {
        const userInRoles: UserInRoles = await getUsers(tenantName);
        const roles: UserRole[] = await getAllUserRoles(tenantName);

        const allRoleUsers: RoleUser[] = Object.values(userInRoles.roles.users)
            .flatMap(roleUsers => roleUsers);

        const roleCounts: Record<string, number> = allRoleUsers.reduce((acc, user) => {
            acc[user.roleName] = (acc[user.roleName] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);

        const rolesWithCount: UserRole[] = roles.filter(role => role.roleName !== "super-user").map(role => ({
            ...role,
            userCount: roleCounts[role.roleName] || 0,
        }));

        setRoles(rolesWithCount);
        setUserInRoles(userInRoles);
    };



    useEffect(() => {
        if (!tenantName) return;
        loadUsers();
    }, [tenantName])


    return (
        <div className="flex-1 space-y-6 p-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">

                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800 rounded-xl flex items-center justify-center border border-purple-200 dark:border-purple-700">
                        <User2Icon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight" data-testid="text-platform-logs-title">
                            User Roles
                        </h1>
                        <p className="text-muted-foreground">
                            Manage built-in and custom user roles                                        </p>
                    </div>
                </div>

                <Button onClick={handleAddRole} className="gap-2" data-testid="button-add-role">
                    <Plus className="w-4 h-4" />
                    Add Custom Role
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Roles</CardTitle>
                        <Settings className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{roles.length}</div>

                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Built-in Roles</CardTitle>
                        <Crown className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{roles.filter(r => !r.customRole).length}</div>
                        <p className="text-xs text-muted-foreground">
                            System roles
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Custom Roles</CardTitle>
                        <Settings className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{roles.filter(r => r.customRole).length}</div>
                        <p className="text-xs text-muted-foreground">
                            Admin created
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                        <Users className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{roles.reduce((sum, role) => sum + role.userCount, 0)}</div>
                        <p className="text-xs text-muted-foreground">
                            Across all roles
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Search and Table */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                            <Shield className="w-5 h-5" />
                            User Roles
                        </CardTitle>
                        <div className="relative w-72">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                            <Input
                                placeholder="Search roles..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                data-testid="input-search-roles"
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Role Name</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead className="w-[280px]">Description</TableHead>
                                    <TableHead>Users</TableHead>
                                    <TableHead>Created By</TableHead>
                                    <TableHead>Created Date</TableHead>
                                    <TableHead className="w-[100px]">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {currentRoles.length > 0 ? (
                                    currentRoles.map((role) => (
                                        <TableRow key={role.id} data-testid={`row-role-${role.id}`}>
                                            <TableCell className="font-medium">
                                                <div className="flex items-center gap-2">
                                                    <Shield className="w-4 h-4 text-primary" />
                                                    {role.roleName}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant={!role.customRole ? "default" : "secondary"}
                                                    className={!role.customRole ? "bg-blue-500 hover:bg-blue-600" : ""}
                                                >
                                                    {!role.customRole ? (
                                                        <>
                                                            <Crown className="w-3 h-3 mr-1" />
                                                            Built-in
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Settings className="w-3 h-3 mr-1" />
                                                            Custom
                                                        </>
                                                    )}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="max-w-[280px] whitespace-normal break-words text-muted-foreground">
                                                {getRoleDescription(role)}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="ghost"
                                                    className="p-0 h-auto text-primary hover:underline justify-start"
                                                    onClick={() => handleViewUsers(role)}
                                                    data-testid={`button-view-users-${role.id}`}
                                                >
                                                    <Users className="w-4 h-4 mr-1" />
                                                    {role.userCount} users
                                                </Button>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {!role.customRole ? "System" : role.createdBy}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {!role.customRole ? "—" : (role.createdAt ? formatDate(role.createdAt) : "—")}
                                            </TableCell>

                                            <TableCell>
                                                {role.customRole ? (
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                className="h-8 w-8 p-0"
                                                                data-testid={`button-actions-${role.id}`}
                                                            >
                                                                <MoreHorizontal className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuItem
                                                                onClick={() => handleEditClick(role)}
                                                                data-testid={`button-edit-${role.id}`}
                                                            >
                                                                <Edit className="mr-2 h-4 w-4" />
                                                                Edit
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem
                                                                onClick={() => handleDeleteClick(role)}
                                                                className="text-destructive"
                                                                data-testid={`button-delete-${role.id}`}
                                                            >
                                                                <Trash2 className="mr-2 h-4 w-4" />
                                                                Delete
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                ) : (
                                                    <span className="text-muted-foreground text-sm">—</span>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} className="p-6">
                                            <EmptyStateCard
                                                embedded
                                                compact
                                                icon={Shield}
                                                title={searchTerm ? "No roles match your search" : "No roles found"}
                                                description={
                                                    searchTerm
                                                        ? "Try adjusting your search terms."
                                                        : "Create a custom role to manage user permissions."
                                                }
                                            />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-4">
                            <div className="text-sm text-muted-foreground">
                                Showing {startIndex + 1} to {Math.min(endIndex, filteredRoles.length)} of {filteredRoles.length} roles
                            </div>
                            <div className="flex items-center space-x-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    data-testid="button-prev-page"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Previous
                                </Button>
                                <span className="text-sm">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    data-testid="button-next-page"
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Role</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete the role "{roleToDelete?.roleName}"? This action cannot be undone.
                            {(roleToDelete?.userCount ?? 0) > 0 && (
                                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800">
                                    Warning: This role is assigned to {roleToDelete?.userCount ?? 0} user(s). Deleting it will remove this role from all assigned users.
                                </div>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleDeleteCancel} data-testid="button-cancel-delete">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteConfirm}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="button-confirm-delete"
                        >
                            Delete Role
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Edit Role Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit className="w-5 h-5" />
                            Edit Role
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="editRoleName">Role Name</Label>
                            <Input
                                id="editRoleName"
                                value={editRoleName}
                                onChange={(e) => setEditRoleName(e.target.value)}
                                placeholder="Enter role name..."
                                data-testid="input-edit-role-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="editRoleDescription">Description</Label>
                            <Input
                                id="editRoleDescription"
                                value={editRoleDescription}
                                onChange={(e) => setEditRoleDescription(e.target.value)}
                                placeholder="Enter role description..."
                                data-testid="input-edit-role-description"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={handleEditCancel}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleEditSave}
                            disabled={!editRoleName.trim()}
                            data-testid="button-save-edit"
                        >
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Role Dialog */}
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Plus className="w-5 h-5" />
                            Add Custom Role
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="newRoleName">Role Name</Label>
                            <Input
                                id="newRoleName"
                                value={newRoleName}
                                onChange={(e) => setNewRoleName(e.target.value)}
                                placeholder="Enter role name..."
                                data-testid="input-new-role-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="newRoleDescription">Description</Label>
                            <Input
                                id="newRoleDescription"
                                value={newRoleDescription}
                                onChange={(e) => setNewRoleDescription(e.target.value)}
                                placeholder="Enter role description..."
                                data-testid="input-new-role-description"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={handleAddCancel}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddSave}
                            disabled={!newRoleName.trim()}
                            data-testid="button-save-new"
                        >
                            Create Role
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Users in Role Dialog */}
            <Dialog open={usersDialogOpen} onOpenChange={setUsersDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle className="flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            Users with Role: {selectedRole?.roleName}
                            <Badge
                                variant={!selectedRole?.customRole ? "default" : "secondary"}
                                className={!selectedRole?.customRole ? "bg-blue-500" : ""}
                            >
                                {!selectedRole?.customRole ? "Built-in" : "Custom"}
                            </Badge>
                        </DialogTitle>
                    </DialogHeader>

                    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
                        {/* Search */}
                        <div className="relative flex-shrink-0">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                            <Input
                                placeholder="Search users..."
                                value={usersSearchTerm}
                                onChange={(e) => {
                                    setUsersSearchTerm(e.target.value);
                                    setUsersCurrentPage(1);
                                }}
                                className="pl-10"
                                data-testid="input-search-users-in-role"
                            />
                        </div>

                        {/* Users Table */}
                        <div className="min-h-0 flex-1 overflow-y-auto rounded-md border">
                            <Table>
                                <TableHeader className="sticky top-0 z-10 bg-background">
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Created At</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {currentUsersInRole.length > 0 ? (
                                        currentUsersInRole.map((user) => (
                                            <TableRow key={user.id} data-testid={`row-user-${user.id}`}>
                                                <TableCell className="font-medium">
                                                    <div className="flex items-center gap-2">
                                                        <User className="w-4 h-4 text-primary" />
                                                        {user.firstName} {user.lastName}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-muted-foreground">
                                                    {user.email}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="default">
                                                        {user.createdAt}
                                                    </Badge>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={3} className="p-6">
                                                <EmptyStateCard
                                                    embedded
                                                    compact
                                                    icon={User}
                                                    title="No users in this role"
                                                    description="No users are currently assigned to this role."
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>

                    <DialogFooter className="flex-shrink-0 border-t pt-4">
                        <div className="flex w-full items-center justify-between gap-4">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                                {filteredUsersInRole.length > 0 && (
                                    <span className="text-sm text-muted-foreground">
                                        Showing {usersStartIndex + 1} to {Math.min(usersEndIndex, filteredUsersInRole.length)} of {filteredUsersInRole.length} users
                                    </span>
                                )}
                                {usersTotalPages > 1 && (
                                    <div className="flex items-center space-x-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleUsersPageChange(usersCurrentPage - 1)}
                                            disabled={usersCurrentPage === 1}
                                            data-testid="button-users-prev-page"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                            Previous
                                        </Button>
                                        <span className="text-sm">
                                            Page {usersCurrentPage} of {usersTotalPages}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleUsersPageChange(usersCurrentPage + 1)}
                                            disabled={usersCurrentPage === usersTotalPages}
                                            data-testid="button-users-next-page"
                                        >
                                            Next
                                            <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                )}
                            </div>

                            <Button variant="outline" onClick={() => setUsersDialogOpen(false)}>
                                Close
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}